import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_BASE, useAuth } from '../App';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';

const MAX_SIZE_MB = 40;

const inputStyle = {
  width: '100%', padding: '9px 12px', border: '0.5px solid #AAB0B8',
  borderRadius: '8px', fontSize: '13px', fontFamily: 'Manrope, sans-serif',
  color: '#22272D', background: '#F4FAFF', outline: 'none',
  marginTop: '6px', boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: '13px', color: '#22272D', fontWeight: '500', display: 'block',
};

function fileSizeMB(bytes) {
  return (bytes / 1024 / 1024).toFixed(2);
}

function getFileExt(filename) {
  return filename.split('.').pop().toLowerCase();
}

function getPreviewMode(file) {
  if (!file) return null;
  const ext = getFileExt(file.name);
  if (ext === 'pdf') return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
  if (['xlsx', 'xls'].includes(ext)) return 'xlsx';
  if (['doc', 'docx'].includes(ext)) return 'docx';
  if (['ppt', 'pptx'].includes(ext)) return 'pptx';
  if (['txt', 'csv', 'json', 'xml', 'md', 'html', 'htm'].includes(ext)) return 'text';
  return 'unsupported';
}

function AddNewPanel({ value, onChange, onAdd, onCancel, adding, error, placeholder }) {
  return (
    <div style={{ marginTop: '10px', padding: '14px', borderRadius: '8px', background: '#F4FAFF', border: '0.5px solid #AAB0B8' }}>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onAdd()}
          placeholder={placeholder}
          autoFocus
          style={{ ...inputStyle, marginTop: 0, flex: 1 }}
        />
        <button type="button" onClick={onAdd} disabled={adding} style={{
          padding: '9px 16px', borderRadius: '8px', border: 'none',
          background: '#F1FF5C', color: '#22272D', fontSize: '13px',
          fontWeight: '600', fontFamily: 'Manrope, sans-serif',
          cursor: adding ? 'not-allowed' : 'pointer', opacity: adding ? 0.7 : 1, whiteSpace: 'nowrap',
        }}>
          {adding ? 'Adding…' : 'Add'}
        </button>
        <button type="button" onClick={onCancel} style={{
          padding: '9px 14px', borderRadius: '8px', border: '0.5px solid #AAB0B8',
          background: 'transparent', color: '#AAB0B8', fontSize: '13px',
          cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
        }}>
          Cancel
        </button>
      </div>
      {error && <div style={{ fontSize: '11px', color: '#FF766B', marginTop: '4px' }}>{error}</div>}
    </div>
  );
}

// ── XLSX Preview ──────────────────────────────────────────────────────────────
function XlsxPreview({ file }) {
  const [sheets, setSheets]       = useState([]);
  const [activeSheet, setActive]  = useState(0);
  const [rows, setRows]           = useState([]);
  const [headers, setHeaders]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  useEffect(() => {
    setLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        setSheets(wb.SheetNames);
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (data.length > 0) {
          setHeaders(data[0]);
          setRows(data.slice(1));
        }
        setLoading(false);
      } catch (err) {
        setError('Failed to parse spreadsheet.');
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  }, [file]);

  const switchSheet = (wb_raw, idx) => {
    setActive(idx);
  };

  const wbRef = useRef(null);
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => {
      wbRef.current = XLSX.read(e.target.result, { type: 'array' });
    };
    reader.readAsArrayBuffer(file);
  }, [file]);

  const handleSheetChange = (idx) => {
    setActive(idx);
    if (!wbRef.current) return;
    const ws = wbRef.current.Sheets[wbRef.current.SheetNames[idx]];
    const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    if (data.length > 0) {
      setHeaders(data[0]);
      setRows(data.slice(1));
    } else {
      setHeaders([]);
      setRows([]);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>Parsing spreadsheet…</div>;
  if (error)   return <div style={{ padding: '40px', textAlign: 'center', color: '#FF766B', fontFamily: 'Manrope, sans-serif' }}>{error}</div>;

  return (
    <div>
      {sheets.length > 1 && (
        <div style={{ display: 'flex', gap: '6px', padding: '12px 20px', borderBottom: '0.5px solid #AAB0B8', overflowX: 'auto' }}>
          {sheets.map((s, i) => (
            <button key={s} type="button" onClick={() => handleSheetChange(i)} style={{
              padding: '5px 14px', borderRadius: '6px', border: 'none', fontSize: '12px',
              fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
              background: i === activeSheet ? '#F1FF5C' : '#F4FAFF',
              color: '#22272D', fontWeight: i === activeSheet ? '600' : '400',
            }}>
              {s}
            </button>
          ))}
        </div>
      )}
      <div style={{ overflowX: 'auto', maxHeight: '520px', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: 'Manrope, sans-serif' }}>
          {headers.length > 0 && (
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={i} style={{
                    padding: '8px 12px', background: '#F4FAFF', color: '#22272D',
                    fontWeight: '600', textAlign: 'left', borderBottom: '1px solid #AAB0B8',
                    whiteSpace: 'nowrap', position: 'sticky', top: 0,
                  }}>
                    {String(h)}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} style={{ background: ri % 2 === 0 ? '#fff' : '#F9FBFF' }}>
                {headers.map((_, ci) => (
                  <td key={ci} style={{
                    padding: '7px 12px', color: '#22272D',
                    borderBottom: '0.5px solid #E8ECEF', whiteSpace: 'nowrap',
                  }}>
                    {String(row[ci] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={headers.length || 1} style={{ padding: '20px', textAlign: 'center', color: '#AAB0B8' }}>
                  No data in this sheet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── DOCX Preview ──────────────────────────────────────────────────────────────
function DocxPreview({ file }) {
  const [html, setHtml]       = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const result = await mammoth.convertToHtml({ arrayBuffer: e.target.result });
        setHtml(result.value);
        setLoading(false);
      } catch (err) {
        setError('Failed to parse document.');
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  }, [file]);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>Parsing document…</div>;
  if (error)   return <div style={{ padding: '40px', textAlign: 'center', color: '#FF766B', fontFamily: 'Manrope, sans-serif' }}>{error}</div>;

  return (
    <div
      style={{
        padding: '28px 36px', maxHeight: '600px', overflowY: 'auto',
        fontSize: '13px', color: '#22272D', fontFamily: 'Georgia, serif', lineHeight: '1.7',
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ── PPTX card ─────────────────────────────────────────────────────────────────
function PptxCard({ file }) {
  return (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📑</div>
      <div style={{ fontSize: '14px', fontWeight: '600', color: '#22272D', fontFamily: 'Sora, sans-serif', marginBottom: '8px' }}>
        {file.name}
      </div>
      <div style={{ fontSize: '12px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif', marginBottom: '4px' }}>
        PPTX file · {fileSizeMB(file.size)} MB
      </div>
      <div style={{
        marginTop: '16px', padding: '10px 20px', borderRadius: '8px',
        background: 'rgba(241,255,92,0.2)', display: 'inline-block',
        fontSize: '12px', color: '#8a9100', fontFamily: 'Manrope, sans-serif',
      }}>
        ✓ File ready to upload — slide preview available after upload
      </div>
    </div>
  );
}

// ── FilePreview ────────────────────────────────────────────────────────────────
function FilePreview({ file, previewUrl, textContent }) {
  const mode = getPreviewMode(file);

  const headerBar = (
    <div style={{
      padding: '14px 20px', borderBottom: '0.5px solid #AAB0B8',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <span style={{ fontSize: '13px', fontWeight: '600', color: '#22272D', fontFamily: 'Manrope, sans-serif' }}>
        Document Preview
      </span>
      <span style={{ fontSize: '12px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>
        {file?.name}
      </span>
    </div>
  );

  if (mode === 'pdf') {
    return (
      <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', overflow: 'hidden' }}>
        {headerBar}
        <iframe src={previewUrl} title="Document Preview" style={{ width: '100%', height: '600px', border: 'none', display: 'block' }} />
      </div>
    );
  }

  if (mode === 'image') {
    return (
      <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', overflow: 'hidden' }}>
        {headerBar}
        <div style={{ padding: '20px', textAlign: 'center', background: '#F4FAFF' }}>
          <img src={previewUrl} alt={file?.name} style={{ maxWidth: '100%', maxHeight: '600px', borderRadius: '8px', objectFit: 'contain' }} />
        </div>
      </div>
    );
  }

  if (mode === 'text') {
    return (
      <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', overflow: 'hidden' }}>
        {headerBar}
        <div style={{ padding: '20px', maxHeight: '600px', overflowY: 'auto' }}>
          <pre style={{ fontSize: '12px', color: '#22272D', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
            {textContent || 'Loading…'}
          </pre>
        </div>
      </div>
    );
  }

  if (mode === 'xlsx') {
    return (
      <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', overflow: 'hidden' }}>
        {headerBar}
        <XlsxPreview file={file} />
      </div>
    );
  }

  if (mode === 'docx') {
    return (
      <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', overflow: 'hidden' }}>
        {headerBar}
        <DocxPreview file={file} />
      </div>
    );
  }

  if (mode === 'pptx') {
    return (
      <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', overflow: 'hidden' }}>
        {headerBar}
        <PptxCard file={file} />
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
      <div style={{ fontSize: '13px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>
        Preview not available for this file type.
      </div>
    </div>
  );
}

// ── Upload ────────────────────────────────────────────────────────────────────
function Upload() {
  const { user } = useAuth();
  const canAddType = !!user;

  const [date,             setDate]             = useState('');
  const [schedules,        setSchedules]        = useState([]);
  const [filteredSchedules,setFilteredSchedules]= useState([]);
  const [scheduleId,       setScheduleId]       = useState(null);
  const [docType,          setDocType]          = useState('');
  const [docTypes,         setDocTypes]         = useState([]);
  const [showAddDocType,   setShowAddDocType]   = useState(false);
  const [newDocTypeName,   setNewDocTypeName]   = useState('');
  const [addingDocType,    setAddingDocType]    = useState(false);
  const [addDocTypeError,  setAddDocTypeError]  = useState('');
  const [meetingType,      setMeetingType]      = useState('');
  const [meetingTypes,     setMeetingTypes]     = useState([]);
  const [showAddMtgType,   setShowAddMtgType]   = useState(false);
  const [newMtgTypeName,   setNewMtgTypeName]   = useState('');
  const [addingMtgType,    setAddingMtgType]    = useState(false);
  const [addMtgTypeError,  setAddMtgTypeError]  = useState('');
  const [file,             setFile]             = useState(null);
  const [previewUrl,       setPreviewUrl]       = useState(null);
  const [textContent,      setTextContent]      = useState('');
  const [message,          setMessage]          = useState('');
  const [messageType,      setMessageType]      = useState('');
  const [loading,          setLoading]          = useState(false);
  const fileRef = useRef(null);

  // Read schedule_id from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const schedIdParam = params.get('schedule_id');
    if (schedIdParam) {
      setScheduleId(parseInt(schedIdParam));
    }
  }, []);

  const loadDocTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/document-types`, { withCredentials: true });
      setDocTypes(res.data);
      if (res.data.length > 0 && !docType) setDocType(res.data[0].name);
    } catch (err) { console.error('Failed to load document types:', err); }
  }, [docType]);

  useEffect(() => { loadDocTypes(); }, [loadDocTypes]);

  const loadMeetingTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/meeting-types`, { withCredentials: true });
      setMeetingTypes(res.data);
      if (res.data.length > 0 && !meetingType) setMeetingType(res.data[0].name);
    } catch (err) { console.error('Failed to load meeting types:', err); }
  }, [meetingType]);

  useEffect(() => { loadMeetingTypes(); }, [loadMeetingTypes]);

  const loadSchedules = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/schedules`, { withCredentials: true });
      setSchedules(res.data);
    } catch (err) { console.error('Failed to load schedules:', err); }
  }, []);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  useEffect(() => {
    if (date) {
      const filtered = schedules.filter(s => s.date === date);
      setFilteredSchedules(filtered);
      // If URL had schedule_id but it's not in today's date, clear it
      if (scheduleId && !filtered.find(s => s.id === scheduleId)) {
        setScheduleId(null);
      }
    } else {
      setFilteredSchedules([]);
      // Don't clear scheduleId if it came from URL
    }
  }, [date, schedules, scheduleId]);

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const handleAddDocType = useCallback(async () => {
    const name = newDocTypeName.trim();
    if (!name) { setAddDocTypeError('Please enter a document type name'); return; }
    if (docTypes.some(t => t.name.toLowerCase() === name.toLowerCase())) { setAddDocTypeError('This document type already exists'); return; }
    setAddingDocType(true); setAddDocTypeError('');
    try {
      await axios.post(`${API_BASE}/api/document-types/add`, { name }, { withCredentials: true });
      await loadDocTypes();
      setDocType(name); setNewDocTypeName(''); setShowAddDocType(false);
    } catch (err) { setAddDocTypeError(err.response?.data?.message || 'Failed to add document type'); }
    finally { setAddingDocType(false); }
  }, [newDocTypeName, docTypes, loadDocTypes]);

  const handleAddMtgType = useCallback(async () => {
    const name = newMtgTypeName.trim();
    if (!name) { setAddMtgTypeError('Please enter a meeting type name'); return; }
    if (meetingTypes.some(t => t.name.toLowerCase() === name.toLowerCase())) { setAddMtgTypeError('This meeting type already exists'); return; }
    setAddingMtgType(true); setAddMtgTypeError('');
    try {
      await axios.post(`${API_BASE}/api/meeting-types/add`, { name }, { withCredentials: true });
      await loadMeetingTypes();
      setMeetingType(name); setNewMtgTypeName(''); setShowAddMtgType(false);
    } catch (err) { setAddMtgTypeError(err.response?.data?.message || 'Failed to add meeting type'); }
    finally { setAddingMtgType(false); }
  }, [newMtgTypeName, meetingTypes, loadMeetingTypes]);

  const handleFileChange = useCallback((e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
      setMessage(`File is too large. Maximum size is ${MAX_SIZE_MB}MB.`);
      setMessageType('error');
      setFile(null); setPreviewUrl(null); setTextContent('');
      fileRef.current.value = '';
      return;
    }
    setMessage('');
    setFile(selected);

    const mode = getPreviewMode(selected);
    setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setTextContent('');

    if (mode === 'pdf' || mode === 'image') {
      setPreviewUrl(URL.createObjectURL(selected));
    } else if (mode === 'text') {
      setPreviewUrl(URL.createObjectURL(selected));
      const reader = new FileReader();
      reader.onload = (ev) => setTextContent(ev.target.result);
      reader.readAsText(selected);
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!date)        { setMessage('Please select an upload date.');   setMessageType('error'); return; }
    if (!meetingType) { setMessage('Please select a meeting type.');   setMessageType('error'); return; }
    if (!docType)     { setMessage('Please select a document type.');  setMessageType('error'); return; }
    if (!file)        { setMessage('Please choose a file to upload.'); setMessageType('error'); return; }

    setLoading(true); setMessage('');
    const formData = new FormData();
    formData.append('date',         date);
    formData.append('type',         docType);
    formData.append('meeting_type', meetingType);
    if (scheduleId) {
      formData.append('schedule_id', scheduleId);
    }
    formData.append('file',         file);

    try {
      await axios.post(`${API_BASE}/api/upload`, formData, { withCredentials: true });
      setMessage('Document uploaded successfully!');
      setMessageType('success');
      setDate(''); setFile(null); setPreviewUrl(null); setTextContent(''); setScheduleId(null);
      fileRef.current.value = '';
    } catch (err) {
      setMessage(err.response?.data?.message || 'Upload failed. Please try again.');
      setMessageType('error');
    } finally { setLoading(false); }
  }, [date, file, docType, meetingType, scheduleId]);

  const handleCancel = useCallback(() => {
    setDate(''); setFile(null); setPreviewUrl(null); setTextContent(''); setScheduleId(null);
    setMessage(''); setMessageType('');
    fileRef.current.value = '';
  }, []);

  const isDisabled = loading || !date || !file || !meetingType || !docType;
  const showPreview = !!file;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '40px' }}>
      <div style={{ width: '100%', maxWidth: '620px' }}>
        <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: '22px', fontWeight: '600', color: '#22272D', marginBottom: '24px' }}>
          Upload a Document
        </h1>

        <div style={{
          background: '#fff', border: '0.5px solid #AAB0B8',
          borderRadius: '12px', padding: '28px', marginBottom: showPreview ? '20px' : '0',
        }}>
          {/* Meeting Type */}
          <div style={{ marginBottom: '18px' }}>
            <label style={labelStyle}>Meeting Type</label>
            <select value={meetingType} onChange={e => {
              if (e.target.value === '__add_new__') setShowAddMtgType(true);
              else { setMeetingType(e.target.value); setShowAddMtgType(false); }
            }} style={inputStyle}>
              {meetingTypes.length === 0 && <option value="">No meeting types yet</option>}
              {meetingTypes.map(mt => <option key={mt.id} value={mt.name}>{mt.name}</option>)}
              {canAddType && <option value="__add_new__">+ Add new meeting type...</option>}
            </select>
            {showAddMtgType && canAddType && (
              <AddNewPanel
                value={newMtgTypeName}
                onChange={v => { setNewMtgTypeName(v); setAddMtgTypeError(''); }}
                onAdd={handleAddMtgType}
                onCancel={() => { setShowAddMtgType(false); setNewMtgTypeName(''); setAddMtgTypeError(''); }}
                adding={addingMtgType} error={addMtgTypeError}
                placeholder="e.g. Executive Committee Meeting"
              />
            )}
          </div>

          {/* Upload Date */}
          <div style={{ marginBottom: '18px' }}>
            <label style={labelStyle}>Upload Date</label>
            <input type="date" value={date} onChange={e => { setDate(e.target.value); }} style={inputStyle} />
          </div>

          {/* Meeting Selection */}
          {filteredSchedules.length > 0 && (
            <div style={{ marginBottom: '18px' }}>
              <label style={labelStyle}>Which meeting are these docs for?</label>
              <select
                value={scheduleId || ''}
                onChange={(e) => setScheduleId(e.target.value ? parseInt(e.target.value) : null)}
                style={inputStyle}
              >
                <option value="">Select a meeting (optional)</option>
                {filteredSchedules.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.meeting_type} — {s.time}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Document Type */}
          <div style={{ marginBottom: '18px' }}>
            <label style={labelStyle}>Document Type</label>
            <select value={docType} onChange={e => {
              if (e.target.value === '__add_new__') setShowAddDocType(true);
              else { setDocType(e.target.value); setShowAddDocType(false); }
            }} style={inputStyle}>
              {docTypes.length === 0 && <option value="">No document types yet</option>}
              {docTypes.map(dt => <option key={dt.id} value={dt.name}>{dt.name}</option>)}
              {canAddType && <option value="__add_new__">+ Add new document type...</option>}
            </select>
            {showAddDocType && canAddType && (
              <AddNewPanel
                value={newDocTypeName}
                onChange={v => { setNewDocTypeName(v); setAddDocTypeError(''); }}
                onAdd={handleAddDocType}
                onCancel={() => { setShowAddDocType(false); setNewDocTypeName(''); setAddDocTypeError(''); }}
                adding={addingDocType} error={addDocTypeError}
                placeholder="e.g. Legal Document"
              />
            )}
          </div>

          {/* File Upload */}
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Choose File</label>
            <div style={{ fontSize: '11px', color: '#AAB0B8', marginTop: '4px', marginBottom: '6px' }}>
              All file types accepted · Max {MAX_SIZE_MB}MB
            </div>
            <input type="file" accept="*" ref={fileRef} onChange={handleFileChange} style={inputStyle} />
            {file && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#AAB0B8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: '#1a6b1a', fontWeight: '600' }}>✓</span>
                {file.name} ({fileSizeMB(file.size)} MB)
              </div>
            )}
          </div>

          {/* Message */}
          {message && (
            <div style={{
              padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
              background: messageType === 'success' ? 'rgba(189,255,192,0.3)' : 'rgba(255,118,107,0.2)',
              color:      messageType === 'success' ? '#1a6b1a'               : '#a83232',
              fontSize: '13px',
            }}>
              {message}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" onClick={handleUpload} disabled={isDisabled} style={{
              flex: 1, background: '#F1FF5C', color: '#22272D', border: 'none',
              padding: '10px 28px', borderRadius: '8px', fontSize: '14px',
              fontWeight: '600', fontFamily: 'Manrope, sans-serif',
              cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.7 : 1,
            }}>
              {loading ? 'Uploading…' : 'Upload Document'}
            </button>
            <button type="button" onClick={handleCancel} disabled={loading} style={{
              flex: 1, background: '#FF766B', color: '#fff', border: 'none',
              padding: '10px 28px', borderRadius: '8px', fontSize: '14px',
              fontWeight: '600', fontFamily: 'Manrope, sans-serif',
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
            }}>
              Cancel
            </button>
          </div>
        </div>

        {/* Preview */}
        {showPreview && (
          <FilePreview file={file} previewUrl={previewUrl} textContent={textContent} />
        )}
      </div>
    </div>
  );
}

export default Upload;